import { Button, Flex, Grid, Input, Text } from '@chakra-ui/react'
import { ColumnDef } from '@tanstack/react-table'
import { Dispatch, SetStateAction, useMemo } from 'react'

import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import { AdminUser } from '../../../services/adminUsersService'
import { formatAdminDateTime } from '../../../utils/dates'

interface UsersAdminFiltersProps {
  filters: {
    email: string
    name: string
    nickname: string
    organization: string
  }
  setFilters: Dispatch<
    SetStateAction<{
      email: string
      name: string
      nickname: string
      organization: string
    }>
  >
  setPaginationPageIndex: (pageIndex: number) => void
}

export const UsersAdminFilters = ({
  filters,
  setFilters,
  setPaginationPageIndex,
}: UsersAdminFiltersProps) => (
  <Grid
    templateColumns={{ base: '1fr', md: 'repeat(4, minmax(0, 1fr)) auto' }}
    gap={3}
    mb={4}
  >
    <Input
      placeholder="Filter email"
      value={filters.email}
      onChange={event => {
        setFilters(current => ({ ...current, email: event.target.value }))
        setPaginationPageIndex(0)
      }}
    />
    <Input
      placeholder="Filter name"
      value={filters.name}
      onChange={event => {
        setFilters(current => ({ ...current, name: event.target.value }))
        setPaginationPageIndex(0)
      }}
    />
    <Input
      placeholder="Filter nickname"
      value={filters.nickname}
      onChange={event => {
        setFilters(current => ({
          ...current,
          nickname: event.target.value,
        }))
        setPaginationPageIndex(0)
      }}
    />
    <Input
      placeholder="Filter organization"
      value={filters.organization}
      onChange={event => {
        setFilters(current => ({
          ...current,
          organization: event.target.value,
        }))
        setPaginationPageIndex(0)
      }}
    />
    <Button
      variant="secondary"
      onClick={() => {
        setFilters({
          email: '',
          name: '',
          nickname: '',
          organization: '',
        })
        setPaginationPageIndex(0)
      }}
    >
      Clear
    </Button>
  </Grid>
)

export const useUsersAdminColumns = (): ColumnDef<AdminUser>[] =>
  useMemo(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: info => (
          <Text fontWeight="medium">{info.getValue() as string}</Text>
        ),
        meta: {
          toText: val => val as string,
        },
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: info => <Text>{(info.getValue() as string) ?? '—'}</Text>,
        meta: {
          toText: val => (val as string) ?? '—',
        },
      },
      {
        accessorKey: 'nickname',
        header: 'Nickname',
        cell: info => <Text>{(info.getValue() as string) ?? '—'}</Text>,
        meta: {
          toText: val => (val as string) ?? '—',
        },
      },
      {
        accessorKey: 'roles',
        header: 'Roles',
        enableSorting: false,
        cell: info => {
          const roles = info.getValue() as string[]
          if (!roles || !Array.isArray(roles) || roles.length === 0)
            return <Text>—</Text>
          return (
            <Flex gap={1} flexWrap="wrap">
              {roles.map((role, idx) => (
                <AdminBadge key={idx} colorScheme="blue" variant="subtle">
                  {role}
                </AdminBadge>
              ))}
            </Flex>
          )
        },
        meta: {
          toText: val => (Array.isArray(val) ? val.join(', ') : '—'),
        },
      },
      {
        accessorKey: 'organizations',
        header: 'Organizations',
        enableSorting: false,
        cell: info => {
          const orgs = info.getValue() as AdminUser['organizations']
          if (!orgs || orgs.length === 0) return <Text>—</Text>
          return (
            <Flex gap={1} flexWrap="wrap">
              {orgs.map(org => (
                <AdminBadge key={org.id} colorScheme="gray" variant="subtle">
                  {org.name}
                </AdminBadge>
              ))}
            </Flex>
          )
        },
        meta: {
          toText: val => {
            const orgs = val as AdminUser['organizations']
            return orgs ? orgs.map(o => o.name).join(', ') : '—'
          },
        },
      },
      {
        accessorKey: 'createdDate',
        header: 'Created',
        cell: info => (
          <Text whiteSpace="nowrap">
            {formatAdminDateTime(info.getValue() as string) ?? '—'}
          </Text>
        ),
        meta: {
          toText: val => formatAdminDateTime(val as string) ?? '—',
        },
      },
    ],
    [],
  )
